import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Save, Eye, Plus, Trash2, ExternalLink, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import ImageUpload from "@/components/ImageUpload";
import "@/styles/marketplace.css";

const TABS = [
  { id: "branding", labelEn: "Branding", labelAr: "الهوية" },
  { id: "hero", labelEn: "Hero Banner", labelAr: "البانر الرئيسي" },
  { id: "stats", labelEn: "Stats & Features", labelAr: "الإحصائيات والمزايا" },
  { id: "about", labelEn: "About", labelAr: "من نحن" },
  { id: "contact", labelEn: "Contact", labelAr: "التواصل" },
  { id: "gallery", labelEn: "Gallery", labelAr: "المعرض" },
  { id: "testimonials", labelEn: "Testimonials", labelAr: "آراء العملاء" },
  { id: "seo", labelEn: "SEO", labelAr: "تحسين محركات البحث" },
  { id: "theme", labelEn: "Theme", labelAr: "الألوان والإعدادات" },
];

// Tailwind class shortcuts that mirror the Marketplace palette so the CMS
// inputs/buttons match the public-site visual language. Pulled into
// constants instead of inlining to keep JSX readable.
const MP_MAROON = "#7A1F3D";
const MP_MAROON_DARK = "#5C1730";
const MP_CREAM = "#FAF5F2";

// Lucide-react icon names allowed for the "why choose us" feature cards.
// Kept short on purpose — the operator picks one from this list to avoid
// arbitrary string typos breaking the icon render on the public site.
const FEATURE_ICONS = [
  "Sparkles", "Award", "Heart", "Shield", "Star", "Crown",
  "Gem", "Leaf", "Clock", "Users", "ThumbsUp", "Smile",
];

const SALON_ID = "default";

export default function WebsiteCMS() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [cms, setCms] = useState(null);
  const [tab, setTab] = useState("branding");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get(`/cms/${SALON_ID}`);
    setCms(r.data);
  };
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setCms((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cms/${SALON_ID}`, cms);
      toast.success(isAr ? "تم الحفظ ✓" : "Saved ✓");
    } catch (e) {
      toast.error(isAr ? "خطأ في الحفظ" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addGallery = () =>
    setCms((c) => ({ ...c, gallery: [...(c.gallery || []), { image_url: "", caption_en: "", caption_ar: "" }] }));
  const removeGallery = (i) =>
    setCms((c) => ({ ...c, gallery: c.gallery.filter((_, idx) => idx !== i) }));
  const updateGallery = (i, k, v) =>
    setCms((c) => ({ ...c, gallery: c.gallery.map((g, idx) => (idx === i ? { ...g, [k]: v } : g)) }));

  const addTestimonial = () =>
    setCms((c) => ({ ...c, testimonials: [...(c.testimonials || []), { customer_name: "", rating: 5, text_en: "", text_ar: "", avatar_url: "" }] }));
  const removeTestimonial = (i) =>
    setCms((c) => ({ ...c, testimonials: c.testimonials.filter((_, idx) => idx !== i) }));
  const updateTestimonial = (i, k, v) =>
    setCms((c) => ({ ...c, testimonials: c.testimonials.map((g, idx) => (idx === i ? { ...g, [k]: v } : g)) }));

  // Public-site v4 — feature cards CRUD helpers.
  const addFeature = () =>
    setCms((c) => ({ ...c, features: [...(c.features || []), { icon: "Sparkles", title_en: "", title_ar: "", desc_en: "", desc_ar: "" }] }));
  const removeFeature = (i) =>
    setCms((c) => ({ ...c, features: (c.features || []).filter((_, idx) => idx !== i) }));
  const updateFeature = (i, k, v) =>
    setCms((c) => ({ ...c, features: (c.features || []).map((f, idx) => (idx === i ? { ...f, [k]: v } : f)) }));

  if (!cms) return (
    <div className="min-h-[60vh] flex items-center justify-center text-stone-500" dir={isAr ? "rtl" : "ltr"}>
      {isAr ? "جاري التحميل..." : "Loading..."}
    </div>
  );

  // === Marketplace-style design tokens ===
  const inputCls = "w-full px-3 py-2.5 rounded-lg bg-white border border-stone-300 text-stone-800 placeholder:text-stone-400 text-sm outline-none focus:border-[#7A1F3D] focus:ring-2 focus:ring-[#7A1F3D]/15 transition";
  const labelCls = "text-xs font-bold uppercase tracking-wider text-stone-600 mb-1.5 block";
  const sectionCardCls = "bg-white rounded-2xl border border-stone-200 p-6 md:p-7";
  const dangerBtnCls = "px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 transition flex items-center justify-center";
  const ghostBtnCls = "inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-stone-300 bg-white hover:bg-stone-50 text-sm font-semibold text-stone-700 transition";

  return (
    <div className="min-h-screen pb-12" style={{ background: MP_CREAM }} data-testid="website-cms-page" dir={isAr ? "rtl" : "ltr"}>
      {/* ====================== Hero header (maroon strip) ====================== */}
      <div className="bg-gradient-to-l from-[#7A1F3D] via-[#5C1730] to-[#7A1F3D] text-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col lg:flex-row lg:items-end gap-5 justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] opacity-75 mb-2 flex items-center gap-2">
              <Globe size={13} />
              {isAr ? "CMS · الموقع التسويقي للصالون" : "CMS · Salon Marketing Site"}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">{isAr ? "إدارة الموقع الإلكتروني" : "Website CMS"}</h1>
            <p className="text-sm opacity-85 mt-2 max-w-xl">{isAr
              ? "تحكم في محتوى الصفحة العامة لصالونك — الهوية، البانر، الإحصائيات، المعرض، التقييمات، والـSEO."
              : "Control your salon's public landing page — branding, hero, stats, gallery, testimonials, and SEO."}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/salon/${SALON_ID}`}
              target="_blank"
              data-testid="cms-preview-link"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-sm font-semibold transition"
            >
              <Eye size={15} /> {isAr ? "معاينة الموقع" : "Preview site"} <ExternalLink size={11} />
            </Link>
            <button
              onClick={save}
              disabled={saving}
              data-testid="cms-save-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#7A1F3D] hover:bg-stone-100 text-sm font-bold shadow-md disabled:opacity-60 transition"
            >
              <Save size={15} /> {saving ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "حفظ التغييرات" : "Save changes")}
            </button>
          </div>
        </div>
      </div>

      {/* ====================== Tab strip (pill style) ====================== */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto">
          <div className="flex gap-2 py-3" data-testid="cms-tab-strip">
            {TABS.map((tt) => (
              <button
                key={tt.id}
                onClick={() => setTab(tt.id)}
                data-testid={`cms-tab-${tt.id}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition ${
                  tab === tt.id
                    ? "bg-[#7A1F3D] text-white shadow-sm"
                    : "text-stone-600 hover:bg-stone-100"
                }`}
              >
                {isAr ? tt.labelAr : tt.labelEn}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ====================== Tab content ====================== */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === "branding" && (
          <div className={sectionCardCls}>
            <SectionHeader title={isAr ? "هوية صالونك" : "Salon Branding"} subtitle={isAr ? "الاسم والشعار اللذان يظهران في كل الصفحات." : "Name and logo shown across all your pages."} />
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className={labelCls}>{isAr ? "الاسم (الإنجليزي)" : "Name (English)"}</label><input className={inputCls} value={cms.name_en} onChange={(e) => setField("name_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "الاسم (العربي)" : "Name (Arabic)"}</label><input className={inputCls} value={cms.name_ar} onChange={(e) => setField("name_ar", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "الشعار/التاجلاين (إنجليزي)" : "Tagline (English)"}</label><input className={inputCls} value={cms.tagline_en} onChange={(e) => setField("tagline_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "الشعار/التاجلاين (عربي)" : "Tagline (Arabic)"}</label><input className={inputCls} value={cms.tagline_ar} onChange={(e) => setField("tagline_ar", e.target.value)} /></div>
              <div className="md:col-span-2"><label className={labelCls}>{isAr ? "الشعار (Logo)" : "Logo"}</label><ImageUpload value={cms.logo_url || ""} onChange={(url) => setField("logo_url", url)} recommendedSize="512×512" category="branding" aspect="aspect-square" testIdPrefix="cms-logo" lang={lang} /></div>
            </div>
          </div>
        )}

        {tab === "hero" && (
          <div className={sectionCardCls}>
            <SectionHeader title={isAr ? "البانر الرئيسي" : "Hero Banner"} subtitle={isAr ? "أول ما يراه الزائر — صورة كبيرة + عنوان قوي + زر للحجز." : "First thing visitors see — a hero image + bold title + booking CTA."} />
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className={labelCls}>{isAr ? "العنوان (إنجليزي)" : "Hero Title (English)"}</label><input className={inputCls} value={cms.hero_title_en} onChange={(e) => setField("hero_title_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "العنوان (عربي)" : "Hero Title (Arabic)"}</label><input className={inputCls} value={cms.hero_title_ar} onChange={(e) => setField("hero_title_ar", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "العنوان الفرعي (إنجليزي)" : "Subtitle (English)"}</label><input className={inputCls} value={cms.hero_subtitle_en} onChange={(e) => setField("hero_subtitle_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "العنوان الفرعي (عربي)" : "Subtitle (Arabic)"}</label><input className={inputCls} value={cms.hero_subtitle_ar} onChange={(e) => setField("hero_subtitle_ar", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "زر الإجراء (إنجليزي)" : "CTA Button (English)"}</label><input className={inputCls} value={cms.hero_cta_en} onChange={(e) => setField("hero_cta_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "زر الإجراء (عربي)" : "CTA Button (Arabic)"}</label><input className={inputCls} value={cms.hero_cta_ar} onChange={(e) => setField("hero_cta_ar", e.target.value)} /></div>
              <div className="md:col-span-2"><label className={labelCls}>{isAr ? "صورة البانر (1920×800)" : "Hero Image (1920×800)"}</label><ImageUpload value={cms.hero_image_url || ""} onChange={(url) => setField("hero_image_url", url)} recommendedSize="1920×800" category="hero" aspect="aspect-[16/7]" testIdPrefix="cms-hero" lang={lang} /></div>
            </div>
          </div>
        )}

        {tab === "stats" && (
          <div className="space-y-6">
            <div className={sectionCardCls}>
              <div className="flex items-center justify-between mb-5">
                <SectionHeader title={isAr ? "شريط الإحصائيات" : "Stats Band"} subtitle={isAr ? "اكتب القيمة كما تريد ظهورها (مثلاً: +5000، 4.9، 10 سنوات)" : "Write the value as you want it shown (e.g., 5000+, 4.9, 10 years)"} inline />
                <ToggleSwitch checked={cms.show_stats !== false} onChange={(v) => setField("show_stats", v)} label={isAr ? "إظهار" : "Show"} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { key: "clients", label: isAr ? "العملاء" : "Clients" },
                  { key: "years", label: isAr ? "الخبرة" : "Years" },
                  { key: "services", label: isAr ? "الخدمات" : "Services" },
                  { key: "rating", label: isAr ? "التقييم" : "Rating" },
                ].map(({ key, label }) => (
                  <div key={key} className="p-4 rounded-xl border border-stone-200 bg-stone-50">
                    <div className="text-xs font-bold text-stone-700 mb-2">{label}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <input className={inputCls} placeholder={isAr ? "القيمة" : "Value"} value={cms[`stats_${key}_value`] || ""} onChange={(e) => setField(`stats_${key}_value`, e.target.value)} data-testid={`cms-stats-${key}-value`} />
                      <input className={inputCls} placeholder={isAr ? "التسمية" : "Label"} value={cms[`stats_${key}_label_${lang}`] || ""} onChange={(e) => setField(`stats_${key}_label_${lang}`, e.target.value)} data-testid={`cms-stats-${key}-label`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={sectionCardCls}>
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <SectionHeader title={isAr ? "لماذا نحن؟ — بطاقات المزايا" : "Why Choose Us — Feature Cards"} subtitle={isAr ? "ينصح بـ 3 أو 4 مزايا فقط." : "3 or 4 features recommended."} inline />
                <div className="flex items-center gap-3">
                  <ToggleSwitch checked={cms.show_features !== false} onChange={(v) => setField("show_features", v)} label={isAr ? "إظهار" : "Show"} />
                  <button onClick={addFeature} data-testid="cms-add-feature" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#7A1F3D] hover:bg-[#5C1730] text-white text-sm font-semibold transition"><Plus size={14} /> {isAr ? "إضافة" : "Add"}</button>
                </div>
              </div>
              {(cms.features || []).length === 0 ? (
                <div className="text-center py-10 text-sm text-stone-400 border-2 border-dashed border-stone-200 rounded-xl">{isAr ? "لا توجد مزايا بعد — أضف أول ميزة بالأعلى" : "No features yet — add your first one above"}</div>
              ) : (
                <div className="space-y-2.5">
                  {(cms.features || []).map((f, i) => (
                    <div key={i} className="grid md:grid-cols-12 gap-2 p-3 rounded-xl border border-stone-200 bg-stone-50">
                      <select className={`${inputCls} md:col-span-2`} value={f.icon || "Sparkles"} onChange={(e) => updateFeature(i, "icon", e.target.value)}>
                        {FEATURE_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                      <input className={`${inputCls} md:col-span-2`} placeholder={isAr ? "العنوان EN" : "Title EN"} value={f.title_en || ""} onChange={(e) => updateFeature(i, "title_en", e.target.value)} />
                      <input className={`${inputCls} md:col-span-2`} placeholder={isAr ? "العنوان AR" : "Title AR"} value={f.title_ar || ""} onChange={(e) => updateFeature(i, "title_ar", e.target.value)} />
                      <input className={`${inputCls} md:col-span-2`} placeholder={isAr ? "الوصف EN" : "Desc EN"} value={f.desc_en || ""} onChange={(e) => updateFeature(i, "desc_en", e.target.value)} />
                      <input className={`${inputCls} md:col-span-3`} placeholder={isAr ? "الوصف AR" : "Desc AR"} value={f.desc_ar || ""} onChange={(e) => updateFeature(i, "desc_ar", e.target.value)} />
                      <button onClick={() => removeFeature(i)} className={`md:col-span-1 ${dangerBtnCls}`}><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={sectionCardCls}>
              <SectionHeader title={isAr ? "شريط الخدمات المميزة" : "Featured Items Strip"} subtitle={isAr ? "إظهار شريط \"خدماتنا المميزة\" على الصفحة الرئيسية" : "Show \"Featured Services\" band on landing"} inline />
              <label className="flex items-center gap-2.5 mt-3 cursor-pointer text-sm font-medium text-stone-700">
                <input type="checkbox" checked={cms.show_featured_items !== false} onChange={(e) => setField("show_featured_items", e.target.checked)} data-testid="cms-show-featured" className="w-4 h-4 accent-[#7A1F3D]" />
                {isAr ? "تفعيل شريط الخدمات المميزة" : "Enable Featured Services band"}
              </label>
              <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">{isAr ? "علّم الخدمات والمنتجات والباقات كـ \"مميزة\" من شاشة إدارتها لتظهر هنا." : "Mark items as \"Featured\" from their admin screens to surface them here."}</p>
            </div>
          </div>
        )}

        {tab === "about" && (
          <div className={sectionCardCls}>
            <SectionHeader title={isAr ? "من نحن" : "About Us"} subtitle={isAr ? "نص ترحيبي يُعرّف الزائر بقصة صالونك." : "A welcome paragraph introducing your salon's story."} />
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div><label className={labelCls}>{isAr ? "العنوان (إنجليزي)" : "Title (English)"}</label><input className={inputCls} value={cms.about_title_en} onChange={(e) => setField("about_title_en", e.target.value)} /></div>
                <div><label className={labelCls}>{isAr ? "العنوان (عربي)" : "Title (Arabic)"}</label><input className={inputCls} value={cms.about_title_ar} onChange={(e) => setField("about_title_ar", e.target.value)} /></div>
              </div>
              <div><label className={labelCls}>{isAr ? "النص (إنجليزي)" : "Text (English)"}</label><textarea rows={5} className={inputCls} value={cms.about_text_en} onChange={(e) => setField("about_text_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "النص (عربي)" : "Text (Arabic)"}</label><textarea rows={5} className={inputCls} value={cms.about_text_ar} onChange={(e) => setField("about_text_ar", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "رابط صورة من نحن" : "About Image URL"}</label><input className={inputCls} value={cms.about_image_url || ""} onChange={(e) => setField("about_image_url", e.target.value)} /></div>
              <ToggleSwitch checked={!!cms.show_about} onChange={(v) => setField("show_about", v)} label={isAr ? "إظهار قسم من نحن" : "Show About section"} />
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className={sectionCardCls}>
            <SectionHeader title={isAr ? "بيانات التواصل" : "Contact Info"} subtitle={isAr ? "العنوان، الهواتف، وسائل التواصل الاجتماعي، والخريطة." : "Address, phones, socials, and the location map."} />
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className={labelCls}>{isAr ? "العنوان (إنجليزي)" : "Address (English)"}</label><input className={inputCls} value={cms.address_en} onChange={(e) => setField("address_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "العنوان (عربي)" : "Address (Arabic)"}</label><input className={inputCls} value={cms.address_ar} onChange={(e) => setField("address_ar", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "الهاتف" : "Phone"}</label><input className={inputCls} value={cms.phone} onChange={(e) => setField("phone", e.target.value)} dir="ltr" /></div>
              <div><label className={labelCls}>{isAr ? "البريد الإلكتروني" : "Email"}</label><input className={inputCls} value={cms.email} onChange={(e) => setField("email", e.target.value)} dir="ltr" /></div>
              <div><label className={labelCls}>{isAr ? "رقم الواتساب" : "WhatsApp Number"}</label><input className={inputCls} value={cms.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} placeholder="97455000000" dir="ltr" /></div>
              <div><label className={labelCls}>Instagram</label><input className={inputCls} value={cms.instagram} onChange={(e) => setField("instagram", e.target.value)} placeholder="@username" dir="ltr" /></div>
              <div><label className={labelCls}>Snapchat</label><input className={inputCls} value={cms.snapchat} onChange={(e) => setField("snapchat", e.target.value)} dir="ltr" /></div>
              <div><label className={labelCls}>TikTok</label><input className={inputCls} value={cms.tiktok} onChange={(e) => setField("tiktok", e.target.value)} dir="ltr" /></div>
              <div><label className={labelCls}>{isAr ? "ساعات العمل (إنجليزي)" : "Working Hours (English)"}</label><input className={inputCls} value={cms.working_hours_en} onChange={(e) => setField("working_hours_en", e.target.value)} /></div>
              <div><label className={labelCls}>{isAr ? "ساعات العمل (عربي)" : "Working Hours (Arabic)"}</label><input className={inputCls} value={cms.working_hours_ar} onChange={(e) => setField("working_hours_ar", e.target.value)} /></div>
              <div className="md:col-span-2"><label className={labelCls}>{isAr ? "رابط Google Maps Embed" : "Google Maps Embed URL"}</label><input className={inputCls} value={cms.map_embed_url} onChange={(e) => setField("map_embed_url", e.target.value)} dir="ltr" /></div>
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className={sectionCardCls}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <SectionHeader title={isAr ? "معرض الصور" : "Photo Gallery"} subtitle={isAr ? "صور صالونك، الأعمال، الأجواء." : "Photos of your salon, work, and ambience."} inline />
              <div className="flex items-center gap-3">
                <ToggleSwitch checked={!!cms.show_gallery} onChange={(v) => setField("show_gallery", v)} label={isAr ? "إظهار" : "Show"} />
                <button onClick={addGallery} data-testid="cms-add-gallery" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#7A1F3D] hover:bg-[#5C1730] text-white text-sm font-semibold transition"><Plus size={14} /> {isAr ? "إضافة صورة" : "Add image"}</button>
              </div>
            </div>
            {(cms.gallery || []).length === 0 ? (
              <div className="text-center py-10 text-sm text-stone-400 border-2 border-dashed border-stone-200 rounded-xl">{isAr ? "لا توجد صور بعد" : "No images yet"}</div>
            ) : (
              <div className="space-y-2.5">
                {(cms.gallery || []).map((g, i) => (
                  <div key={i} className="grid md:grid-cols-12 gap-2 p-3 rounded-xl border border-stone-200 bg-stone-50">
                    <div className="md:col-span-5"><ImageUpload value={g.image_url} onChange={(url) => updateGallery(i, "image_url", url)} recommendedSize="800×800" category="gallery" aspect="aspect-square" testIdPrefix={`cms-gallery-${i}`} lang={lang} /></div>
                    <input className={`${inputCls} md:col-span-3 self-start`} placeholder="Caption EN" value={g.caption_en} onChange={(e) => updateGallery(i, "caption_en", e.target.value)} />
                    <input className={`${inputCls} md:col-span-3 self-start`} placeholder={isAr ? "التعليق AR" : "Caption AR"} value={g.caption_ar} onChange={(e) => updateGallery(i, "caption_ar", e.target.value)} />
                    <button onClick={() => removeGallery(i)} className={`md:col-span-1 self-start ${dangerBtnCls}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "testimonials" && (
          <div className={sectionCardCls}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <SectionHeader title={isAr ? "آراء العملاء" : "Customer Testimonials"} subtitle={isAr ? "اقتباسات حقيقية من عميلاتك السعيدات." : "Real quotes from your happiest clients."} inline />
              <div className="flex items-center gap-3">
                <ToggleSwitch checked={!!cms.show_testimonials} onChange={(v) => setField("show_testimonials", v)} label={isAr ? "إظهار" : "Show"} />
                <button onClick={addTestimonial} data-testid="cms-add-testimonial" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#7A1F3D] hover:bg-[#5C1730] text-white text-sm font-semibold transition"><Plus size={14} /> {isAr ? "إضافة" : "Add"}</button>
              </div>
            </div>
            {(cms.testimonials || []).length === 0 ? (
              <div className="text-center py-10 text-sm text-stone-400 border-2 border-dashed border-stone-200 rounded-xl">{isAr ? "لا توجد تقييمات بعد" : "No testimonials yet"}</div>
            ) : (
              <div className="space-y-2.5">
                {(cms.testimonials || []).map((tt, i) => (
                  <div key={i} className="grid md:grid-cols-12 gap-2 p-3 rounded-xl border border-stone-200 bg-stone-50">
                    <input className={`${inputCls} md:col-span-3`} placeholder={isAr ? "اسم العميل" : "Customer Name"} value={tt.customer_name} onChange={(e) => updateTestimonial(i, "customer_name", e.target.value)} />
                    <input type="number" min={1} max={5} className={`${inputCls} md:col-span-1`} value={tt.rating} onChange={(e) => updateTestimonial(i, "rating", parseInt(e.target.value || 5))} />
                    <input className={`${inputCls} md:col-span-3`} placeholder={isAr ? "النص (إنجليزي)" : "Text (English)"} value={tt.text_en} onChange={(e) => updateTestimonial(i, "text_en", e.target.value)} />
                    <input className={`${inputCls} md:col-span-3`} placeholder={isAr ? "النص (عربي)" : "Text (Arabic)"} value={tt.text_ar} onChange={(e) => updateTestimonial(i, "text_ar", e.target.value)} />
                    <input className={`${inputCls} md:col-span-1`} placeholder={isAr ? "رابط الصورة" : "Avatar URL"} value={tt.avatar_url} onChange={(e) => updateTestimonial(i, "avatar_url", e.target.value)} dir="ltr" />
                    <button onClick={() => removeTestimonial(i)} className={`md:col-span-1 ${dangerBtnCls}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "seo" && (
          <div className={sectionCardCls}>
            <SectionHeader title={isAr ? "تحسين محركات البحث (SEO)" : "Search Engine Optimization"} subtitle={isAr ? "إعدادات تُحدّد كيف يظهر صالونك على Google والشبكات الاجتماعية." : "Controls how your salon appears on Google and social media."} />
            <div className="rounded-xl p-4 bg-amber-50 border border-amber-200 text-sm text-amber-900 mb-5">
              {isAr
                ? "هذه الإعدادات تظهر فقط على الصفحات العامة (الموقع التسويقي للصالون). لن تظهر على لوحة الإدارة."
                : "These settings apply ONLY to public marketing pages — never internal dashboards."}
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{isAr ? "الـSlug (رابط مختصر)" : "Salon Slug (friendly URL)"}</label>
                <input className={inputCls} placeholder="isaloon-doha" value={cms.slug || ""} onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9\u0600-\u06FF-]/g, "-").replace(/-+/g, "-"))} data-testid="cms-seo-slug" dir="ltr" />
                <p className="text-[11px] text-stone-500 mt-1.5">{isAr ? "سيُستخدم في /salons/your-slug" : "Used in /salons/your-slug"}</p>
              </div>
              <div>
                <label className={labelCls}>{isAr ? "صورة OG (للمشاركة)" : "OG Cover Image"}</label>
                <ImageUpload value={cms.cover_url || ""} onChange={(url) => setField("cover_url", url)} recommendedSize="1200×630" category="seo" aspect="aspect-[1200/630]" testIdPrefix="cms-cover" lang={lang} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>{isAr ? "وصف ميتا مخصص (160 حرف)" : "Custom Meta Description (160 chars)"}</label>
                <textarea
                  className={inputCls}
                  rows={2}
                  maxLength={170}
                  value={cms.seo_description || ""}
                  onChange={(e) => setField("seo_description", e.target.value)}
                  placeholder={isAr ? "اتركه فارغاً للتوليد التلقائي..." : "Leave empty for auto-generated..."}
                  data-testid="cms-seo-description"
                />
                <div className="text-[10px] text-stone-500 mt-1 text-end num-display">{(cms.seo_description || "").length}/160</div>
              </div>
              <div>
                <label className={labelCls}>{isAr ? "كلمات مفتاحية بالعربية" : "Arabic Keywords"}</label>
                <textarea className={inputCls} rows={4} value={cms.seo_keywords_ar || ""} onChange={(e) => setField("seo_keywords_ar", e.target.value)} placeholder="صالون نسائي في الدوحة, أفضل صالون قطر, ..." data-testid="cms-seo-keywords-ar" dir="rtl" />
                <p className="text-[11px] text-stone-500 mt-1.5">{isAr ? "مفصولة بفاصلة، 5-10 كلمات" : "Comma-separated, 5-10 keywords"}</p>
              </div>
              <div>
                <label className={labelCls}>{isAr ? "كلمات مفتاحية إنجليزية" : "English Keywords"}</label>
                <textarea className={inputCls} rows={4} value={cms.seo_keywords_en || ""} onChange={(e) => setField("seo_keywords_en", e.target.value)} placeholder="beauty salon in Doha, best ladies salon Qatar, ..." data-testid="cms-seo-keywords-en" dir="ltr" />
                <p className="text-[11px] text-stone-500 mt-1.5">Comma-separated, 5-10 keywords</p>
              </div>
              <div>
                <label className={labelCls}>{isAr ? "نطاق الأسعار" : "Price Range"}</label>
                <select className={inputCls} value={cms.price_range || "$$"} onChange={(e) => setField("price_range", e.target.value)} data-testid="cms-seo-price-range">
                  <option value="$">$ — Budget</option>
                  <option value="$$">$$ — Standard</option>
                  <option value="$$$">$$$ — Premium</option>
                  <option value="$$$$">$$$$ — Luxury</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{isAr ? "ساعات العمل (Schema.org)" : "Opening Hours (Schema.org)"}</label>
                <input className={inputCls} placeholder="Sa-Th 10:00-23:00" value={cms.opening_hours || ""} onChange={(e) => setField("opening_hours", e.target.value)} data-testid="cms-seo-hours" dir="ltr" />
              </div>
            </div>
            <div className="rounded-xl p-4 mt-6 border border-stone-200 bg-stone-50">
              <h3 className="text-sm font-bold text-stone-800 mb-2">{isAr ? "نصائح سريعة" : "Quick Tips"}</h3>
              <ul className="text-xs text-stone-600 space-y-1.5 list-disc ms-5 leading-relaxed">
                <li>{isAr ? "اكتب 5-10 كلمات مفتاحية لكل لغة. المزيد قد يُعتبر spam." : "5-10 keywords per language. More may flag as spam."}</li>
                <li>{isAr ? "اذكر مدينتك وحيك (مثلاً: \"صالون في الدوحة - الدفنة\")" : "Mention your city + district for local SEO."}</li>
                <li>{isAr ? "صورة OG بحجم 1200×630 بكسل لأفضل عرض على وسائل التواصل." : "OG image 1200×630 px for best social shares."}</li>
                <li>{isAr ? "وصف الميتا الأمثل: 120-160 حرفاً، يحتوي على كلمتين مفتاحيتين." : "Meta description: 120-160 chars with 2 main keywords."}</li>
              </ul>
            </div>
          </div>
        )}

        {tab === "theme" && (
          <div className={sectionCardCls}>
            <SectionHeader title={isAr ? "ألوان الموقع والإعدادات العامة" : "Site Colors & General Settings"} subtitle={isAr ? "اختر الألوان المستخدمة في موقعك التسويقي." : "Pick the colors used in your public marketing site."} />
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>{isAr ? "اللون الرئيسي" : "Primary Color"}</label>
                <div className="flex items-center gap-3">
                  <input type="color" className="w-16 h-12 rounded-lg cursor-pointer border border-stone-300" value={cms.primary_color} onChange={(e) => setField("primary_color", e.target.value)} />
                  <input className={inputCls} value={cms.primary_color || ""} onChange={(e) => setField("primary_color", e.target.value)} dir="ltr" />
                </div>
              </div>
              <div>
                <label className={labelCls}>{isAr ? "لون التمييز" : "Accent Color"}</label>
                <div className="flex items-center gap-3">
                  <input type="color" className="w-16 h-12 rounded-lg cursor-pointer border border-stone-300" value={cms.accent_color} onChange={(e) => setField("accent_color", e.target.value)} />
                  <input className={inputCls} value={cms.accent_color || ""} onChange={(e) => setField("accent_color", e.target.value)} dir="ltr" />
                </div>
              </div>
              <div className="md:col-span-2 pt-2">
                <ToggleSwitch checked={!!cms.show_contact} onChange={(v) => setField("show_contact", v)} label={isAr ? "إظهار قسم التواصل" : "Show Contact section"} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Inline helpers — kept local so the file stays self-contained.
// ============================================================================
function SectionHeader({ title, subtitle, inline = false }) {
  if (inline) {
    return (
      <div className="min-w-0">
        <h2 className="text-lg font-bold text-stone-800">{title}</h2>
        {subtitle && <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">{subtitle}</p>}
      </div>
    );
  }
  return (
    <div className="mb-5 pb-4 border-b border-stone-100">
      <h2 className="text-xl font-bold text-stone-800">{title}</h2>
      {subtitle && <p className="text-sm text-stone-500 mt-1.5 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function ToggleSwitch({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer text-sm font-medium text-stone-700">
      <span
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChange(!checked); } }}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition cursor-pointer ${checked ? "bg-[#7A1F3D]" : "bg-stone-300"}`}
      >
        <span className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
      <span>{label}</span>
    </label>
  );
}

